import {
  defaultsRetroactiveWaitingPeriodReimbursement,
  isNullOrUndefined,
  toStateAbbreviation,
} from '@shared';

interface PolicyExtractionOptions {
  policyType?: PolicyFundingSource['policyType'] | null;
}

export function policyExtractedDataToPolicyState(
  processedResponse: ParsedPolicyDataExtractResponse,
  currentPolicyState: Partial<PolicyFundingSource>,
  options: PolicyExtractionOptions = {},
): PolicyFundingSource {
  const extractedData = {} as Partial<PolicyFundingSource>;

  if (!isNullOrUndefined(options.policyType)) {
    extractedData.policyType = options.policyType;
  }

  const divideByHundred = (value: number) => value / 100;

  const setFieldValue = (
    parsedField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
    policyFieldKey: PolicyFieldKey,
    transformValue?: (value: any) => any,
  ) => {
    if (parsedField && !isNullOrUndefined(parsedField.parsedFieldValue)) {
      extractedData[policyFieldKey] = transformValue
        ? transformValue(parsedField.parsedFieldValue)
        : parsedField.parsedFieldValue;
    }
  };

  setFieldValue(
    processedResponse.policyMinimumGuaranteedBenefitAmount,
    'policyMinimumGuaranteedBenefitAmount',
  );
  setFieldValue(processedResponse.policyLumpSumPayment, 'policyLumpSumPayment');
  // Carriers print the issue state as a full name ("Issued in: New York"); every
  // state field we store and compare uses the abbreviation. An unrecognised
  // value normalises to '' rather than being stored as-is, so a bad extraction
  // falls through to the ZIP-derived default instead of poisoning the lookup.
  setFieldValue(processedResponse.issuedInState, 'issuedInState', value =>
    toStateAbbreviation(String(value)) || null,
  );
  setFieldValue(
    processedResponse.policyInflationProtection,
    'policyInflationProtection',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyPremiumMonthlyCost, 'policyPremiumMonthlyCost');
  setFieldValue(processedResponse.policyPremiumStartYear, 'policyPremiumStartYear');

  setFieldValue(processedResponse.policyMaximumBenefitAmount, 'policyMaximumBenefitAmount');
  setFieldValue(
    processedResponse.dailyAssistedLivingBenefitAmount,
    'dailyAssistedLivingBenefitAmount',
  );

  setFieldValue(processedResponse.dailyHomeBenefitAmount, 'dailyHomeBenefitAmount');

  setFieldValue(
    processedResponse.dailyIndependentLivingBenefitAmount,
    'dailyIndependentLivingBenefitAmount',
  );

  setFieldValue(processedResponse.dailyNursingHomeBenefitAmount, 'dailyNursingHomeBenefitAmount');
  setFieldValue(processedResponse.monthlyHomeBenefitAmount, 'monthlyHomeBenefitAmount');
  setFieldValue(
    processedResponse.monthlyAssistedLivingBenefitAmount,
    'monthlyAssistedLivingBenefitAmount',
  );
  setFieldValue(
    processedResponse.monthlyIndependentLivingBenefitAmount,
    'monthlyIndependentLivingBenefitAmount',
  );
  setFieldValue(
    processedResponse.monthlyNursingHomeBenefitAmount,
    'monthlyNursingHomeBenefitAmount',
  );
  setFieldValue(processedResponse.homeCareWaitingPeriodDays, 'homeCareWaitingPeriodDays');
  setFieldValue(processedResponse.facilityCareWaitingPeriodDays, 'facilityCareWaitingPeriodDays');
  setFieldValue(processedResponse.policyLimitedPayYears, 'policyLimitedPayYears');
  setFieldValue(processedResponse.policyBenefitPeriodMonths, 'policyBenefitPeriodMonths');
  setFieldValue(processedResponse.homeCareBenefitPeriodMonths, 'homeCareBenefitPeriodMonths');
  setFieldValue(
    processedResponse.policyContinuationBenefitAmount,
    'policyContinuationBenefitAmount',
  );
  setFieldValue(
    processedResponse.policyMonthlyWithdrawalRate,
    'policyMonthlyWithdrawalRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyPremiumIncreaseRate,
    'policyPremiumIncreaseRate',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyPremiumIncreaseYears, 'policyPremiumIncreaseYears');
  setFieldValue(
    processedResponse.policyInflationProtectionOnFaceAmount,
    'policyInflationProtectionOnFaceAmount',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyInflationProtectionOnCOBAmount,
    'policyInflationProtectionOnCOBAmount',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyInflationProtectionYears, 'policyInflationProtectionYears');
  setFieldValue(
    processedResponse.policyBaseBenefitAmountAtIssue,
    'policyBaseBenefitAmountAtIssue',
  );
  setFieldValue(
    processedResponse.policyContinuationBenefitAmountAtIssue,
    'policyContinuationBenefitAmountAtIssue',
  );
  setFieldValue(
    processedResponse.policyAccelerationBenefitDurationMonths,
    'policyAccelerationBenefitDurationMonths',
  );
  setFieldValue(
    processedResponse.policyEobBenefitDurationMonths,
    'policyEobBenefitDurationMonths',
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedInitialRate,
    'policyBaseBenefitGuaranteedInitialRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedInitialRateYears,
    'policyBaseBenefitGuaranteedInitialRateYears',
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedRenewalRate,
    'policyBaseBenefitGuaranteedRenewalRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyMonthlyContinuationBenefitChargeRate,
    'policyMonthlyContinuationBenefitChargeRate',
  );
  setFieldValue(
    processedResponse.policyContinuationBenefitFirstAnniversaryRate,
    'policyContinuationBenefitFirstAnniversaryRate',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyAccumulatedValueAtIssue, 'policyAccumulatedValueAtIssue');
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedInitialRate,
    'policyAccumulatedValueGuaranteedInitialRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedInitialRateYears,
    'policyAccumulatedValueGuaranteedInitialRateYears',
  );
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedRenewalRate,
    'policyAccumulatedValueGuaranteedRenewalRate',
    divideByHundred,
  );
  if (processedResponse.isPrimaryPolicyHolder?.fieldValue) {
    extractedData.isPrimaryPolicyHolder =
      processedResponse.isPrimaryPolicyHolder.fieldValue.toLowerCase() === 'yes';
  }

  if (processedResponse.policyPaymentType?.fieldValue) {
    extractedData.isIndemnityPolicyPayment =
      processedResponse.policyPaymentType.fieldValue.toLowerCase() === 'indemnity';
  }
  setFieldValue(processedResponse.policyIndemnityBenefitPercent, 'policyIndemnityBenefitPercent');

  if (processedResponse.policyInflationProtectionType?.fieldValue) {
    extractedData.isSimpleInflationProtection =
      processedResponse.policyInflationProtectionType.fieldValue.toLowerCase() === 'simple';
  }

  // Upload classifies the policy type itself, so it never passes through the
  // type dropdown that seeds this election (`usePolicyForm.handleChangePolicyField`)
  // — and nothing extracts it. Without the same seeding here, an imported
  // CareMatters policy stores a null election and cmTwoStrategy skips the
  // retroactive elimination-period lump. Never clobber an explicit choice.
  const importedPolicyType = extractedData.policyType ?? currentPolicyState.policyType;
  if (
    isNullOrUndefined(currentPolicyState.hasRetroactiveWaitingPeriodReimbursement) &&
    defaultsRetroactiveWaitingPeriodReimbursement(importedPolicyType)
  ) {
    extractedData.hasRetroactiveWaitingPeriodReimbursement = true;
  }

  return {
    ...currentPolicyState,
    ...extractedData,
  } as PolicyFundingSource;
}
